import { HTTPClient, HTTPMethod, HTTPResponse } from "@/core/common/communication/HTTPClient";
import PerformanceDS from "../domain/PerformanceDS";
import PerformancesRepository from "../domain/PerformancesRepository";
import { LoadPerformancesResponse, PerformancesOperarionsResponse } from "../domain/PerformancesOperationsResponse";
import {
  DELETE_PERFORMANCE_ERROR,
  LOADING_PERFORMANCES_ERROR,
  SAVE_PERFORMANCE_ERROR,
  UPDATE_PERFORMANCE_ERROR
} from "@/core/common/utils/ResourceStrings";

interface APIResponse<T> {
  result: boolean;
  data?: T;
  detail?: string | object;
}

export default class PerformancesRepositoryImpl extends PerformancesRepository {
  private httpClient: HTTPClient;
  private allPerformances: Map<string, PerformanceDS>;
  private performancesLoaded: boolean;
  private activePerformances: Array<string>;

  constructor(httpClient: HTTPClient) {
    super();
    this.httpClient = httpClient;
    this.performancesLoaded = false;
    this.allPerformances = new Map();
    this.activePerformances = new Array<string>();
  }

  private getDetail(detail: string | object | undefined): string {
    if (!detail) return "";
    return typeof detail === "string" ? detail : JSON.stringify(detail);
  }

  public getPerformanceById(id: string): PerformanceDS {
    let result = {} as PerformanceDS;
    if (this.allPerformances.has(id)) result = this.allPerformances.get(id) as PerformanceDS;
    return result;
  }

  private doIndexPerformances(performances: Array<PerformanceDS>) {
    this.allPerformances = new Map();
    this.activePerformances = new Array<string>();
    performances.forEach((performance) => {
      this.allPerformances.set(performance.id, performance);
      if (!performance.deleted) this.activePerformances.push(performance.id);
    });
  }

  public async loadPerformances(): Promise<LoadPerformancesResponse> {
    const result = {
      performances: new Array<PerformanceDS>(),
      success: true,
      errorMsg: ""
    };
    if (!this.performancesLoaded) {
      this.performancesLoaded = false;
      const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.GET, "/api/performance/list", {});
      const apiResponse = httpResponse.data as APIResponse<Array<PerformanceDS>>;

      if (httpResponse.success && apiResponse.result) {
        this.performancesLoaded = true;
        result.performances = apiResponse.data || new Array<PerformanceDS>();
        this.doIndexPerformances(result.performances);
      } else {
        result.success = false;
        result.errorMsg = httpResponse.success
          ? `${LOADING_PERFORMANCES_ERROR}${this.getDetail(apiResponse.detail)}`
          : `${LOADING_PERFORMANCES_ERROR}${httpResponse.errorMsg}`;
        this.performancesLoaded = false;

        console.error("PerformancesRepositoryImpl/loadPerformances: " + result.errorMsg);
      }
    }

    return Promise.resolve(result);
  }

  public getAllPerformancesIds(): Array<string> {
    return Array.from(this.allPerformances.keys());
  }

  public getPefromancesMap(): Map<string, PerformanceDS> {
    return this.allPerformances;
  }

  public getActivePerformancesIds(): Array<string> {
    return this.activePerformances;
  }

  public async deletePerformances(ids: Array<string>): Promise<PerformancesOperarionsResponse> {
    const result = {
      success: true,
      errorMsg: ""
    };

    for (const performanceId of ids) {
      const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/performance/delete", {
        id: performanceId
      });
      const apiResponse = httpResponse.data as APIResponse<object>;

      if (!httpResponse.success || !apiResponse.result) {
        result.success = false;
        result.errorMsg = httpResponse.success
          ? `${DELETE_PERFORMANCE_ERROR}${this.getDetail(apiResponse.detail)}`
          : `${DELETE_PERFORMANCE_ERROR}${httpResponse.errorMsg}`;
        break;
      }

      const performance = this.allPerformances.get(performanceId);
      if (performance) {
        performance.deleted = true;
        const toDeleteIndex = this.activePerformances.indexOf(performance.id);
        if (toDeleteIndex > -1) {
          this.activePerformances.splice(toDeleteIndex, 1);
        }
      }
    }

    return Promise.resolve(result);
  }

  public async updatePerformance(performance: PerformanceDS): Promise<PerformancesOperarionsResponse> {
    const result = {
      success: true,
      errorMsg: ""
    };

    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/performance/update", {
      performance
    });
    const apiResponse = httpResponse.data as APIResponse<object>;

    if (!httpResponse.success || !apiResponse.result) {
      result.success = false;
      result.errorMsg = httpResponse.success
        ? `${UPDATE_PERFORMANCE_ERROR}${this.getDetail(apiResponse.detail)}`
        : `${UPDATE_PERFORMANCE_ERROR}${httpResponse.errorMsg}`;
    } else this.allPerformances.set(performance.id, performance);

    return Promise.resolve(result);
  }
  public async savePerformance(performance: PerformanceDS): Promise<PerformancesOperarionsResponse> {
    const result = {
      success: true,
      errorMsg: ""
    };

    const httpResponse: HTTPResponse = await this.httpClient.send(HTTPMethod.POST, "/api/performance/create", {
      performance
    });
    const apiResponse = httpResponse.data as APIResponse<object>;

    if (!httpResponse.success || !apiResponse.result) {
      result.success = false;
      result.errorMsg = httpResponse.success
        ? `${SAVE_PERFORMANCE_ERROR}${this.getDetail(apiResponse.detail)}`
        : `${SAVE_PERFORMANCE_ERROR}${httpResponse.errorMsg}`;
    } else {
      this.allPerformances.set(performance.id, performance);
      this.activePerformances.push(performance.id);
    }

    return Promise.resolve(result);
  }
}
